
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import CategorySelection from "@/components/subscription/CategorySelection";
import TimeSlotManager from "@/components/subscription/TimeSlotManager";
import TimezoneSelector from "@/components/subscription/TimezoneSelector";
import { useSubscriberManager } from "@/hooks/useSubscriberManager";
import { Category } from "@/types/category";
import { TimeSlot } from "@/types/subscription";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EditSubscriberFormProps {
  subscriberId: string;
  initialCategories: string[];
  initialTimeSlots: TimeSlot[];
  initialTimezone: string;
  allCategories: Category[];
  onClose: () => void;
}

export default function EditSubscriberForm({
  subscriberId,
  initialCategories,
  initialTimeSlots,
  initialTimezone,
  allCategories,
  onClose
}: EditSubscriberFormProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(initialTimeSlots);
  const [timezone, setTimezone] = useState(initialTimezone);
  
  const { toast } = useToast();
  const { updateSubscriberPreferences, isLoading } = useSubscriberManager();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedCategories.length === 0) {
      toast({
        title: "Please select categories",
        description: "You must select at least one category of quotes.",
        variant: "destructive",
      });
      return;
    }
    
    if (timeSlots.length === 0) {
      toast({
        title: "Please add delivery times",
        description: "You must add at least one delivery time.",
        variant: "destructive",
      });
      return;
    }

    updateSubscriberPreferences.mutate({
      userId: subscriberId,
      data: {
        selectedCategories,
        timeSlots,
        timezone
      }
    }, {
      onSuccess: () => {
        onClose();
      }
    });
  };

  return (
    <ScrollArea className="h-[70vh] pr-4">
      <form onSubmit={handleSubmit} className="space-y-6 py-4">
        <CategorySelection 
          selectedCategories={selectedCategories} 
          setSelectedCategories={setSelectedCategories} 
        />
        
        <TimezoneSelector 
          timezone={timezone} 
          setTimezone={setTimezone} 
        />
        
        <TimeSlotManager 
          timeSlots={timeSlots} 
          setTimeSlots={setTimeSlots} 
          selectedCategories={selectedCategories}
        />
        
        <div className="flex justify-end gap-2 pt-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Subscription'}
          </Button>
        </div>
      </form>
    </ScrollArea>
  );
}
